import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import RemoveIcon from "@mui/icons-material/Remove";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useCallback, useRef, useState, type ReactNode } from "react";

interface FloatingPanelProps {
  title: ReactNode;
  children: ReactNode;
  defaultPosition?: { x: number; y: number };
  defaultMinimized?: boolean;
  minWidth?: number;
  maxWidth?: number;
  /** Caps the body and lets it scroll. For panels with more in them than the map is tall. */
  maxHeight?: number | string;
  /** Left off where the content brings its own padding. */
  disableContentPadding?: boolean;
  /**
   * Where the panel sits.
   *
   * `"draggable"` starts at `defaultPosition` and follows the pointer. A pinned corner cannot be
   * dragged: a panel that opens in the same place every time is one the reader finds without
   * looking, which is worth more than being able to move it, and it stays out of the way of the
   * panels that own the other corners.
   */
  placement?: PanelPlacement;
  onClose?: () => void;
}

export type PanelPlacement = "draggable" | "bottomRight" | "topRight";

/** Where each pinned corner sits, clear of the map's own controls. */
const PINNED_OFFSETS: Record<Exclude<PanelPlacement, "draggable">, object> = {
  bottomRight: { right: 8, bottom: 8 },
  topRight: { right: 56, top: 56 },
};

export default function FloatingPanel({
  title,
  children,
  defaultPosition = { x: 12, y: 74 },
  defaultMinimized = false,
  minWidth = 280,
  maxWidth = 400,
  maxHeight,
  disableContentPadding = false,
  placement = "draggable",
  onClose,
}: FloatingPanelProps) {
  const isDraggable = placement === "draggable";
  const [position, setPosition] = useState(defaultPosition);
  const [isMinimized, setIsMinimized] = useState(defaultMinimized);
  const dragRef = useRef<{ startX: number; startY: number } | null>(null);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).closest("button")) return;
      dragRef.current = {
        startX: e.clientX - position.x,
        startY: e.clientY - position.y,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [position],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragRef.current) return;
      const maxX = window.innerWidth - minWidth;
      const maxY = window.innerHeight - 40;
      setPosition({
        x: Math.max(0, Math.min(e.clientX - dragRef.current.startX, maxX)),
        y: Math.max(0, Math.min(e.clientY - dragRef.current.startY, maxY)),
      });
    },
    [minWidth],
  );

  const handlePointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  return (
    <Paper
      elevation={4}
      sx={{
        position: "absolute",
        ...(isDraggable ? { left: position.x, top: position.y } : PINNED_OFFSETS[placement]),
        zIndex: 900,
        minWidth,
        maxWidth,
        maxHeight,
        pointerEvents: "auto",
        userSelect: "none",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        onPointerDown={isDraggable ? handlePointerDown : undefined}
        onPointerMove={isDraggable ? handlePointerMove : undefined}
        onPointerUp={isDraggable ? handlePointerUp : undefined}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.5,
          cursor: isDraggable ? (dragRef.current ? "grabbing" : "grab") : "default",
          bgcolor: "action.hover",
          borderBottom: isMinimized ? "none" : 1,
          borderColor: "divider",
        }}
      >
        {isDraggable ? (
          <DragIndicatorIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
        ) : null}
        <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ flex: 1 }}>
          {title}
        </Typography>
        <IconButton size="small" onClick={() => setIsMinimized(m => !m)}>
          <RemoveIcon fontSize="small" />
        </IconButton>
        {onClose && (
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {!isMinimized && (
        <Box sx={{ p: disableContentPadding ? 0 : 1.5, minHeight: 0, overflowY: "auto" }}>
          {children}
        </Box>
      )}
    </Paper>
  );
}
